import { PlusIcon } from '@heroicons/react/24/outline';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import { useAutoFocus } from '@/data/hook/useAutoFocus';
import { StudioPermissions } from '@/enums/VersioningStatus';
import { TempChapter } from '@/models/Chapter';
import { bodyText } from '@/styles/styles';
import { getDisabledDirectLessons } from '@/utils/courseStudio/directLessons';
import { CourseStudioFormFields } from '../CourseStudio.hooks';
import DirectLessonBookChapter from './DirectLessonBookChapter';

type Props = {
  lessonIndex: number;
  studioPermissions: StudioPermissions;
  startAllOpen?: boolean;
};

export default function DirectLessonBookChapters(props: Props) {
  const { lessonIndex, studioPermissions, startAllOpen } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const { control } = useFormContext<CourseStudioFormFields>();

  const {
    fields: chapters,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `directLessons.${lessonIndex}.chapters`,
    keyName: 'tempId',
  });

  const disabled = getDisabledDirectLessons(studioPermissions);

  function handleAddChapter() {
    const newChapter = new TempChapter();
    append(newChapter);
  }

  const { setElementRef } = useAutoFocus(chapters.length);

  return (
    <fieldset className="flex flex-col gap-2.5">
      <ConditionalRenderer
        condition={chapters.length > 0}
        fallback={<p className={bodyText}>{t('noChapters')}</p>}
      >
        <ul className="flex flex-col gap-2.5">
          {chapters.map((chapter, index) => (
            <DirectLessonBookChapter
              key={chapter.tempId || chapter.id}
              lessonIndex={lessonIndex}
              chapterIndex={index}
              chapter={chapter}
              studioPermissions={studioPermissions}
              removeChapter={() => remove(index)}
              setChapterElementRef={setElementRef}
              startAllOpen={startAllOpen}
            />
          ))}
        </ul>
      </ConditionalRenderer>

      <ConditionalWrapper
        condition={disabled}
        wrapper={
          <Tooltip
            text={t('notAllowedIfNotAtLeastInLimitedEdition')}
            classNameContainer="flex w-full"
          />
        }
      >
        <MainButton
          color="custom"
          className="w-full p-3 text-primary font-400"
          text={t('addChapter')}
          icon={<PlusIcon />}
          onClick={handleAddChapter}
          disabled={disabled}
        />
      </ConditionalWrapper>
    </fieldset>
  );
}
